import React, { useEffect, useRef } from 'react';
import { notification } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useIntl } from 'umi';
import {
  getSocket,
  onCouponStockExhausted,
  offCouponStockExhausted,
  type CouponStockExhaustedAlert,
} from '@/utils/socket';
import { useAuthStore } from '@/stores/authStore';

/**
 * Operator alert for checkouts blocked by an exhausted coupon.
 *
 * On 2026-08-01 between 23:18 and 23:27 KST a live coupon ran out and fifteen checkouts
 * failed in nine minutes. The member saw an untranslated "Insufficient coupon quantity"
 * and kept retrying; nobody on the operations side was told anything at all. The member
 * cannot resolve it — their coupon is valid, the campaign simply has nothing left — so
 * this has to reach an operator, who can raise the issued quantity or end the campaign.
 *
 * Stays on screen until the operator clicks X (`duration: 0`), because the whole point is
 * that it must not be missed while nobody is watching. Repeat attempts by the same member
 * on the same coupon update the existing card (keyed by alertId) with an attempt count
 * instead of stacking fifteen identical ones.
 *
 * Unacknowledged alerts are kept in sessionStorage and re-opened after a refresh; closing
 * with X drops them for good.
 */

const STORAGE_KEY = 'couponStockAlerts';

type StoredAlert = CouponStockExhaustedAlert & { attempts: number };

const readStored = (): StoredAlert[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStored = (alerts: StoredAlert[]): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(alerts));
  } catch {
    // Storage full or blocked — the on-screen alert still works.
  }
};

const CouponStockAlerts: React.FC = () => {
  const intl = useIntl();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [api, contextHolder] = notification.useNotification();
  // Ref, not state: showAlert runs from a socket callback and must always see the
  // current counts without re-subscribing the listener on every alert.
  const attemptsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!isAuthenticated) return;

    const t = (id: string, values?: Record<string, unknown>) =>
      intl.formatMessage({ id }, values as any);

    const dismiss = (alertId: string) => {
      delete attemptsRef.current[alertId];
      writeStored(readStored().filter((a) => a.alertId !== alertId));
    };

    const showAlert = (alert: StoredAlert) => {
      const rows: Array<[string, React.ReactNode]> = [
        [
          t('component.couponStockAlert.member'),
          `${alert.userName || '-'}${alert.userUniqueId ? ` (${alert.userUniqueId})` : ''}`,
        ],
        [
          t('component.couponStockAlert.coupon'),
          `${alert.couponName}${alert.couponCode ? ` [${alert.couponCode}]` : ''}`,
        ],
        [
          t('component.couponStockAlert.remaining'),
          `${alert.remainingQuantity} / ${t('component.couponStockAlert.attempts', { count: alert.attempts })}`,
        ],
      ];

      if (alert.items.length > 0) {
        const shown = alert.items.map((i) => `${i.title || i.offerId} ×${i.quantity}`).join(', ');
        const hidden = alert.totalItemCount - alert.items.length;
        rows.push([
          t('component.couponStockAlert.products'),
          hidden > 0
            ? `${shown} ${t('component.couponStockAlert.andMore', { count: hidden })}`
            : shown,
        ]);
      }
      if (alert.orderTotalKRW > 0) {
        rows.push([
          t('component.couponStockAlert.orderTotal'),
          `${alert.orderTotalKRW.toLocaleString()}원`,
        ]);
      }

      api.warning({
        key: alert.alertId,
        message: t('component.couponStockAlert.title'),
        icon: <WarningOutlined style={{ color: '#faad14' }} />,
        // 0 = never auto-close. The operator dismisses it with X once they have acted.
        duration: 0,
        placement: 'topRight',
        style: { width: 460 },
        onClose: () => dismiss(alert.alertId),
        description: (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            {rows.map(([label, value]) => (
              <div key={label} style={{ display: 'flex', gap: 8 }}>
                <span style={{ flex: '0 0 84px', opacity: 0.65 }}>{label}</span>
                <span style={{ flex: 1, wordBreak: 'break-word' }}>{value}</span>
              </div>
            ))}
            <div style={{ marginTop: 8, fontWeight: 500 }}>
              {t('component.couponStockAlert.action')}
            </div>
          </div>
        ),
      });
    };

    const handleAlert = (incoming: CouponStockExhaustedAlert) => {
      if (!incoming?.alertId) return;
      const attempts = (attemptsRef.current[incoming.alertId] ?? 0) + 1;
      attemptsRef.current[incoming.alertId] = attempts;

      const merged: StoredAlert = { ...incoming, attempts };
      writeStored([...readStored().filter((a) => a.alertId !== merged.alertId), merged]);
      // Same key → antd replaces the open card rather than stacking a new one.
      showAlert(merged);
    };

    // Re-open anything the operator has not dismissed yet (page refresh, navigation).
    readStored().forEach((alert) => {
      attemptsRef.current[alert.alertId] = alert.attempts;
      showAlert(alert);
    });

    // The socket is created by app.tsx, whose effect may run after this one.
    let pollId: ReturnType<typeof setInterval> | undefined;
    const attach = (): boolean => {
      if (!getSocket()) return false;
      onCouponStockExhausted(handleAlert);
      return true;
    };
    if (!attach()) {
      pollId = setInterval(() => {
        if (attach() && pollId) clearInterval(pollId);
      }, 1000);
    }

    return () => {
      if (pollId) clearInterval(pollId);
      offCouponStockExhausted(handleAlert);
    };
  }, [isAuthenticated, api, intl]);

  return contextHolder;
};

export default CouponStockAlerts;
